// English versions of the built-in content. Items edited in /admin can carry
// their own `en` object, which always wins over these defaults.

export const PORTFOLIO_EN = {
  lokross: {
    category: 'Branding & Identity',
    workType: 'Visual identity',
    description: 'Lokross is a blend of geometric precision and premium visuals — an optically balanced monogram that works on any medium.',
    longDescription: 'We built a solid geometric structure where the optical balance of circles and lines creates a premium identity. The logo adapts to everything from construction helmets to an iOS app icon. Special focus went into the Block B sales campaign, featuring a dynamic 3D "drape" effect.',
    features: ['Optically balanced grid', 'Monogram architecture', '3D poster series']
  },
  'west-dev': {
    category: 'Industrial branding',
    workType: 'Visual identity',
    description: 'An urban, construction-driven identity built on 3D green-container aesthetics and yellow caution-tape details.',
    longDescription: 'For West Development we created an industrial identity with real character. The logo geometry expresses precision, while the chosen typography (Bebas Neue + Helvetica Neue) signals a classic, hard-working and stable business.',
    features: ['Clear typographic hierarchy', 'Grid-based geometry', 'Dynamic social media banners']
  },
  foodly: {
    category: 'Mobile app & mascot',
    workType: 'Visual identity',
    description: 'A friendly panda illustration and a clever logo that merges a map pin with a fork, in an energetic orange-and-blue contrast.',
    longDescription: 'The Foodly logo combines a location pin and a fork — a direct hint at delivery. The panda mascot builds an emotional bond with users, while bright colours and UI elements lift conversion and make the app inviting.',
    features: ['Animated mascot', 'Illustrated packaging', 'Clickable UI structure']
  },
  panorama: {
    category: 'Premium campaigns',
    workType: 'Social media',
    description: 'A historical collage of Kutaisi, a large car giveaway and premium architectural visuals created in collaboration with Solo.',
    longDescription: 'Our collage artwork for Panorama draws on 3,500 years of Kutaisi history for strong emotional impact. For SOLO CH 51 we captured the feel of the premium segment through a refined balance of dark blue, gold and white, with clean architectural renders.',
    features: ['High-end typography', 'Emotional visual narrative', 'Sales-driven renders']
  },
  education: {
    title: 'Educational posters',
    category: 'Image & philosophy series',
    workType: 'Social media',
    description: '“Don’t let the chance slip away” — a mystical, motivational campaign built on the stories of Beethoven, Jordan, Tesla and Armstrong.',
    longDescription: 'The series rests on a strong idea: “Imagine if Beethoven’s deafness had been the end…”. Dark, mystical lighting, dramatic typography and twisted text hold the viewer’s gaze until the brand’s message is read to the end.',
    features: ['Deep storytelling', 'Cinematic design', 'High organic engagement']
  },
  'beone-apres-ski': {
    title: 'BeOne Après-Ski campaign',
    category: 'Social media & seasonal campaign',
    workType: 'Social media',
    description: 'Vintage, cinematic visuals for a Goderdzi seasonal offer, blending the emotion of travel with a clear sales message.',
    longDescription: 'For BeOne we built a multi-format social campaign: apartment offers, a 20% discount message and a strong après-ski mood. Nostalgic textures, warm tones and dynamic typography make the posts memorable, informative and conversion-focused at the same time.',
    features: ['Packaging of seasonal offers', 'Unified carousel & card system', 'Visuals + sales messaging']
  },
  morika: {
    title: 'Morika',
    category: 'Branding & visual identity',
    workType: 'Visual identity',
    description: 'A calm, interior-driven identity with warm textures, a refined colour palette and presentation-ready brand materials.',
    longDescription: 'Morika’s visual system is built on warm textures, interior aesthetics and a natural palette. The case brings together brand marks, environment visuals and applied materials that give the brand a calm, refined and trustworthy character.',
    features: ['Warm, natural colour system', 'Interior-tailored brand presentation', 'Consistent applied materials']
  },
  abica: {
    category: 'Branding & packaging design',
    workType: 'Visual identity',
    description: 'An energetic food-brand identity with standout typography, a packaging system and social media elements.',
    longDescription: 'Abica is built on a bold, loud and memorable visual language. The orange-green contrast, distinctive Georgian typography, packaging design and digital elements make the brand instantly recognisable and commercially strong.',
    features: ['Energetic colour contrast', 'Packaging + digital visual system', 'Memorable typographic character']
  },
  zenari: {
    title: 'Zenari',
    category: 'Branding & real estate',
    workType: 'Visual identity',
    description: 'A premium identity for a property developer — dark blue and gold, with architectural and digital touchpoints.',
    longDescription: 'Zenari is all about the feeling of premium real estate: dark blue backgrounds, a gold logotype, and construction and digital touchpoints forming a trustworthy, high-end visual language. The presentation unites exterior visuals, branded work materials and social formats.',
    features: ['Premium dark-blue & gold contrast', 'Applied developer brand touchpoints', 'One style across digital and physical']
  },
  'athome-ge': {
    category: 'Social media campaign',
    workType: 'Social media',
    description: 'Energetic ad visuals for Athome.ge, built to communicate tech offers.',
    longDescription: 'For Athome.ge we created a high-engagement card series that brings offer, product and a clear call-to-action into one frame — designed for fast reading and strong performance on mobile.',
    features: ['Ad card series', 'Offer-focused visuals', 'Mobile-first formats']
  },
  mochiko: {
    title: 'Mochiko',
    category: 'Content design & SMM',
    workType: 'Social media',
    description: 'A colourful, emotional, product-focused content pack for a dessert brand.',
    longDescription: 'We built a unified content system for Mochiko with the focus on taste, seasonal offers and delivery channels. Every card is designed to grow both brand awareness and orders.',
    features: ['Product-focused communication', 'Visual pack for offers', 'On-brand colour style']
  },
  'hakken-restaurant': {
    title: 'Hakken Restaurant',
    category: 'Restaurant ad campaign',
    workType: 'Social media',
    description: 'Dramatic, high-contrast social media designs made for restaurant content.',
    longDescription: 'The Hakken project combines a strong character-driven style with dramatised product visuals. The result is a card series that grabs attention fast and sells menu items effectively.',
    features: ['Visual packaging of the menu', 'On-brand-tone cards', 'Sales-driven messaging']
  }
};

export const WORK_TYPE_EN = {
  'ვიზუალური იდენტობა': 'Visual identity',
  'სოც. მედია': 'Social media'
};

export const PACKAGE_EN = {
  'ლოგო დიზაინი': { title: 'Logo design', badge: 'Startup', desc: 'A starter package for startups and new projects that need a fast, quality identity.' },
  'ვიზუალური იდენტობა': { title: 'Visual identity', badge: 'Popular', desc: 'For growing businesses that want a strong, professional position in the market.' },
  'სრული ბრენდინგი': { title: 'Full branding', badge: 'Eshelon Premium', desc: 'Our most complete package — full identity and strategy, starting from the name.' },
  Basic: { badge: 'Essential', desc: 'For a steady online presence and well-organised social channels.' },
  Premium: { badge: 'Recommended', desc: 'The sweet spot: 10 posts + 10 stories and full ad management.' },
  Ultimate: { badge: 'Maximum', desc: 'Full marketing support and maximum reach with up-to-date strategies.' }
};

export const FEATURE_EN = {
  'ლოგოს დიზაინი ერთ ენაზე (ქართული ან ინგლისური)': 'Logo in one language (Georgian or English)',
  'ლოგოს ადაპტაცია: ჰორიზონტალური, ვერტიკალური და ავატარის ფორმატი': 'Horizontal, vertical and avatar versions',
  'ძირითადი ფერების პალიტრა': 'Core colour palette',
  'ლოგოს გამოყენების მოკლე წესები (სივრცე, ზომა, ფონი)': 'Short logo usage rules (spacing, size, backgrounds)',
  'ვიზუალური სტილის აღწერა': 'Visual style guide',
  'სრული ბრენდბუქი & სტრატეგია': 'Full brand book & strategy',
  'ლოგოს სრული ფორმა (ძირითადი ნიშანი + ტექსტური ბლოკი)': 'Complete logo (symbol + wordmark)',
  'ლოგოს ადაპტაცია: ჰორიზონტალური, ვერტიკალური და სოციალური მედიის ავატარი': 'Horizontal, vertical and social avatar versions',
  'სრული ფერთა პალიტრა': 'Full colour palette',
  'ლოგო ორივე ენაზე (ქართული და ინგლისური)': 'Logo in both Georgian and English',
  'ლოგოს გამოყენების წესები': 'Logo usage rules',
  'ბრენდის ტიპოგრაფიის სისტემა': 'Brand typography system',
  'ვიზუალური სტილი & ელემენტები': 'Visual style & graphic elements',
  'ბრენდის სახელი (Naming) & სლოგანი': 'Brand name (naming) & slogan',
  'ბრენდის ტონი (Tone of Voice) & სტრატეგია': 'Tone of voice & strategy',
  'ლოგოს სრული არქიტექტურა და ყველა საჭირო ადაპტაცია (ციფრული + ბეჭდური)': 'Full logo system with all digital + print adaptations',
  'ფერები, ტიპოგრაფია & უნიკალური გრიდები': 'Colours, typography & custom grids',
  'შეფუთვისა და კორპორატიული ატრიბუტიკა': 'Packaging & corporate stationery',
  'სრული ბრენდ-არქიტექტურის წიგნი': 'Complete brand book',
  'ნებისმიერ სოციალური მედიის პაკეტზე': 'Discount on any social media package',
  'რეკლამის მართვა': 'Ad management',
  'კონტენტ კალენდარი': 'Content calendar',
  'Shadow რეკლამების ტესტირება': 'Shadow ad testing',
  'ყოველთვიური დეტალური რეპორტინგი': 'Detailed monthly reporting'
};

export const NEWS_EN = {
  'fb-1': { title: 'YOUR CHARACTER SPEAKS FOR YOUR BRAND', excerpt: 'Emotional visuals, strong character and a clear message — content that grabs attention in the first second.', readTime: '2 min' },
  'fb-2': { title: 'STRATEGY OR DESIGN? BOTH', excerpt: 'DYNOCONCEPT aesthetics and art direction in one campaign, where the brand’s voice and visuals work as one.', readTime: '3 min' },
  'fb-3': { title: 'I CAN’T WIN ANYMORE — THE STRATEGY STARTS HERE', excerpt: 'A shock message, red dynamics and conversion-focused copy that moves the audience to act.', readTime: '2 min' },
  'fb-4': { title: 'BIG IDEAS WITH A COSMIC VIEW', excerpt: 'A minimal, cinematic frame and a deep thought — a post that delivers the brand’s philosophy quietly but powerfully.', readTime: '4 min' }
};

export const COLLAB_EN = {
  offers: {
    full: { title: 'Full SMM service', subtitle: 'Design + content + ad management' },
    design: { title: 'Graphic design team', subtitle: 'Design support only' },
    marketing: { title: 'Marketing team', subtitle: 'Ads + optimisation only' }
  },
  terms: [
    'Communication with the agency only (no direct contact with the client)',
    'Up to 2 revision rounds per month',
    '100% prepayment before each month starts',
    '12-month non-solicitation after the partnership ends',
    'Public mention of the collaboration + 10% discount for the partner'
  ]
};

export const SETTINGS_EN = {
  tagline: 'Branding & social media agency · Tbilisi',
  address: 'Tbilisi & Kutaisi, Georgia',
  responseTime: 'We reply within 24 hours',
  pricingNote: 'Prices are indicative. Every company gets its own offer — the cost isn’t based only on the number of posts or on results: it covers strategy, design and our team’s full involvement.',
  statLabels: {
    'კმაყოფილი კლიენტი': 'happy clients',
    'შექმნილი იდენტობა': 'identities created',
    'ზრდა გაყიდვებში': 'sales growth'
  }
};

export function priceEn(price) {
  return String(price || '').replace(/\s*\/\s*თვეში/g, ' / month').replace(/თვეში/g, 'month');
}
